import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../layouts/AppShell";
import { appModules } from "../components/global";
import { GET, POST, PUT, loading, alert, toFixed, profile, Modal, format_date, saveAs } from "../components/CustomComponent";
import PayslipDocument, { downloadPayslipPdf, getPayslipPdfBlob } from "../components/PayslipDocument";
import JSZip from "jszip";

// แผนผังสถานะรอบเงินเดือน
const STATUS_MAP = {
  pending: { label: "รออนุมัติ", color: "#e5a93e", bg: "rgba(229,169,62,0.12)" },
  success: { label: "สำเร็จ", color: "#1f9d57", bg: "rgba(31,157,87,0.12)" },
  cancelled: { label: "ยกเลิก", color: "#e54848", bg: "rgba(229,72,72,0.12)" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || { label: status || "-", color: "#6b8198", bg: "#eef2f6" };
  return (
    <span className="badge rounded-pill px-3 py-2" style={{ color: s.color, background: s.bg, fontWeight: 600 }}>
      {s.label}
    </span>
  );
};

export default function Payroll() {
  const navigate = useNavigate();
  const { title, description } = appModules["payroll"];
  const [data, setData] = useState([]);
  const [viewData, setViewData] = useState(null);
  const [showView, setShowView] = useState(false);
  const [company, setCompany] = useState(null);
  const [slipPayroll, setSlipPayroll] = useState(null);
  const [showSlipList, setShowSlipList] = useState(false);
  const [slipItem, setSlipItem] = useState(null);
  const [showSlipView, setShowSlipView] = useState(false);
  const slipRef = useRef(null);
  // ---- ดาวน์โหลดสลิปทั้งรอบเป็น zip (render ทีละคนผ่าน node ซ่อน แล้วเก็บเป็น pdf ทีละไฟล์) ----
  const zipSlipRef = useRef(null);
  const zipResolveRef = useRef(null); // resolve ของ promise ที่รอ node ซ่อน render สลิปคนถัดไป
  const [zipPayroll, setZipPayroll] = useState(null);
  const [zipItem, setZipItem] = useState(null);
  const [zipProgress, setZipProgress] = useState(null); // { uid, current, total } ของรอบที่กำลังทำอยู่

  // อนุมัติ/ยกเลิกได้เฉพาะผู้ใช้ตำแหน่ง CEO
  const isCEO = (profile?.employee?.position || "").toUpperCase() === "CEO";

  const GetData = async () => {
    loading(true);
    const result = await GET("v1/admin/payrolls", { page: 0 });
    if (result?.status) {
      setData(result.data || []);
    }
    loading(false);
  };

  const GetCompany = async () => {
    const result = await GET("v1/admin/companies", {});
    if (result?.status) setCompany(result.data || null);
  };

  useEffect(() => {
    GetData();
    GetCompany();
  }, []);

  // เปิด modal รายการสลิปของรอบนั้น (ดึงรายละเอียดพร้อม items)
  const openSlipList = async (row) => {
    loading(true);
    const result = await GET("v1/admin/payrolls", { uid: row.uid });
    loading(false);
    if (result?.status) {
      setSlipPayroll(result.data);
      setShowSlipList(true);
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "ไม่สามารถดึงข้อมูลสลิปได้");
    }
  };

  const openSlipView = (item) => {
    setSlipItem(item);
    setShowSlipView(true);
  };

  const handleDownloadSlip = async () => {
    if (!slipRef.current) return;
    loading(true);
    try {
      await downloadPayslipPdf(slipRef.current, `payslip-${slipItem?.employee_name || "employee"}.pdf`);
    } catch (e) {
      console.error("payslip download error:", e);
      alert("", "warning", "แจ้งเตือน", `สร้างไฟล์ PDF ไม่สำเร็จ: ${e?.message || e}`);
    }
    loading(false);
  };

  // รอให้ node ซ่อน render สลิปคนถัดไปเสร็จก่อนค่อย capture
  useEffect(() => {
    if (!zipItem || !zipResolveRef.current) return undefined;
    const resolve = zipResolveRef.current;
    zipResolveRef.current = null;
    const timer = setTimeout(resolve, 60);
    return () => clearTimeout(timer);
  }, [zipItem]);

  // ชื่อไฟล์ต้องปลอดภัยกับ zip: ตัดอักขระที่ใช้เป็นตัวคั่น path ออก
  const safeFileName = (name) => String(name || "").replace(/[\\/:*?"<>|]/g, "_").trim() || "payslip";

  const downloadSlipsZip = async (row) => {
    loading(true);
    const result = await GET("v1/admin/payrolls", { uid: row.uid });
    loading(false);
    if (!result?.status) {
      return alert("", "warning", "แจ้งเตือน", result?.message || "ไม่สามารถดึงข้อมูลสลิปได้");
    }

    const payroll = result.data;
    const items = payroll?.items || [];
    if (!items.length) return alert("", "warning", "แจ้งเตือน", "รอบนี้ยังไม่มีสลิปเงินเดือน");

    const total = items.length;
    if (!(await alert("", "info", "ดาวน์โหลดสลิปทั้งหมด", `จะรวมสลิปเงินเดือน ${total} ไฟล์ (1 พนักงาน = 1 ไฟล์ PDF) เป็นไฟล์ zip เดียว`, "ดาวน์โหลด", "ยกเลิก"))) return;

    let failed = true;
    setZipPayroll(payroll);
    setZipProgress({ uid: row.uid, current: 0, total });
    try {
      const zip = new JSZip();
      const used = {}; // กันชื่อไฟล์ชนกันตอนพนักงานชื่อซ้ำ
      for (let i = 0; i < total; i += 1) {
        const it = items[i];
        setZipProgress({ uid: row.uid, current: i + 1, total });
        // ต้องทำทีละคนตามลำดับ เพราะใช้ node ซ่อนตัวเดียวร่วมกัน
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          zipResolveRef.current = resolve;
          setZipItem(it);
        });
        // eslint-disable-next-line no-await-in-loop
        const blob = await getPayslipPdfBlob(zipSlipRef.current);
        const base = safeFileName(`สลิปเงินเดือน_${it.employee?.employee_code || ""}_${it.employee_name || ""}`.replace(/_+/g, "_"));
        used[base] = (used[base] || 0) + 1;
        zip.file(`${base}${used[base] > 1 ? `_${used[base]}` : ""}.pdf`, blob);
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `${safeFileName(`สลิปเงินเดือน_${payroll.payroll_code || row.payroll_code || ""}_${payroll.period_month || ""}`)}.zip`);
      failed = false;
    } catch (e) {
      console.error("payslip zip error:", e); // แจ้งเตือนผู้ใช้หลังเคลียร์ progress ด้านล่าง
    } finally {
      zipResolveRef.current = null;
      setZipItem(null);
      setZipPayroll(null);
      setZipProgress(null);
    }
    if (failed) await alert("", "warning", "แจ้งเตือน", "สร้างไฟล์ zip ไม่สำเร็จ");
  };

  const sendSlipResult = (result) => {
    if (result?.status) {
      const d = result.data || {};
      alert("", "success", "สำเร็จ", `ส่งอีเมลสำเร็จ ${d.sent || 0} ฉบับ${d.skipped ? ` · ไม่มีอีเมล ${d.skipped}` : ""}${d.failed ? ` · ล้มเหลว ${d.failed}` : ""}`);
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "ส่งอีเมลไม่สำเร็จ (ตรวจการตั้งค่าอีเมลของระบบ)");
    }
  };

  const handleSendOne = async (item) => {
    if (!item.employee?.email) {
      return alert("", "warning", "แจ้งเตือน", "พนักงานคนนี้ไม่มีอีเมล");
    }
    if (await alert("", "info", "ส่งสลิปทางอีเมล", `ส่งสลิปให้ "${item.employee_name}" (${item.employee.email})?`, "ส่ง", "ยกเลิก")) {
      loading(true);
      const result = await POST(`v1/admin/payrolls/${slipPayroll.uid}/send-slips`, { uid: slipPayroll.uid, item_uid: item.uid });
      loading(false);
      sendSlipResult(result);
    }
  };

  const handleSendAll = async () => {
    if (await alert("", "info", "ส่งสลิปทั้งหมด", "ส่งสลิปเงินเดือนให้พนักงานทุกคนที่มีอีเมล?", "ส่งทั้งหมด", "ยกเลิก")) {
      loading(true);
      const result = await POST(`v1/admin/payrolls/${slipPayroll.uid}/send-slips`, { uid: slipPayroll.uid });
      loading(false);
      sendSlipResult(result);
    }
  };

  const handleView = async (row) => {
    loading(true);
    const result = await GET("v1/admin/payrolls", { uid: row.uid });
    loading(false);
    if (result?.status) {
      setViewData(result.data);
      setShowView(true);
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "ไม่สามารถดึงรายละเอียดได้");
    }
  };

  const handleApprove = async (row) => {
    if (await alert("", "info", "อนุมัติรอบเงินเดือน", `ยืนยันอนุมัติ "${row.name || row.payroll_code}" ?`, "อนุมัติ", "ยกเลิก")) {
      loading(true);
      const result = await PUT(`v1/admin/payrolls/${row.uid}/approve`, { uid: row.uid });
      loading(false);
      if (result?.status) {
        alert("", "success", "สำเร็จ", "อนุมัติรอบเงินเดือนสำเร็จ");
        GetData();
      } else {
        alert("", "warning", "แจ้งเตือน", result?.message || "ไม่สามารถอนุมัติได้");
      }
    }
  };

  const handleReject = async (row) => {
    if (await alert("", "warning", "ยกเลิกรอบเงินเดือน", `ยืนยันยกเลิก "${row.name || row.payroll_code}" ?`, "ยกเลิกรอบ", "ปิด")) {
      loading(true);
      const result = await PUT(`v1/admin/payrolls/${row.uid}/reject`, { uid: row.uid });
      loading(false);
      if (result?.status) {
        alert("", "success", "สำเร็จ", "ยกเลิกรอบเงินเดือนแล้ว");
        GetData();
      } else {
        alert("", "warning", "แจ้งเตือน", result?.message || "ไม่สามารถยกเลิกได้");
      }
    }
  };

  const pendingCount = data.filter((d) => d.status === "pending").length;
  const paidNet = data.filter((d) => d.status === "success").reduce((s, d) => s + (Number(d.total_net) || 0), 0);

  const summaryCards = [
    { label: "รอบเงินเดือนทั้งหมด", value: String(data.length), color: "#0c76e5" },
    { label: "รออนุมัติ", value: String(pendingCount), color: "#e5a93e" },
    { label: "ยอดจ่ายสุทธิ (อนุมัติแล้ว)", value: `฿ ${toFixed(paidNet)}`, color: "#1f9d57" },
  ];

  return (
    <AppShell title={title} description={description}>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <h4 className="fw-bold mb-0 text-dark">รอบเงินเดือน</h4>
          <span className="small text-secondary">{description}</span>
        </div>
        <button onClick={() => navigate("/payroll/create")} className="btn btn-primary px-4">
          <i className="fa-solid fa-plus me-2" />สร้างรอบเงินเดือน
        </button>
      </div>

      <div className="row g-3 mb-4">
        {summaryCards.map((item) => (
          <div key={item.label} className="col-12 col-md-4">
            <div
              className="h-100 rounded-4 p-4 d-flex flex-column justify-content-center"
              style={{
                background: "linear-gradient(180deg, #ffffff 0%, #f2f9ff 100%)",
                border: "1px solid rgba(18, 125, 255, 0.10)",
                boxShadow: "0 14px 28px rgba(12, 118, 229, 0.06)",
              }}
            >
              <p className="small text-uppercase text-secondary mb-1">{item.label}</p>
              <h2 className="h3 mb-0 fw-bold" style={{ color: item.color }}>{item.value}</h2>
            </div>
          </div>
        ))}
      </div>

      <div
        className="rounded-4 p-3 bg-white"
        style={{ border: "1px solid rgba(18, 125, 255, 0.10)", boxShadow: "0 4px 12px rgba(12, 118, 229, 0.04)" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h5 className="mb-0 fw-semibold text-dark">รายการรอบเงินเดือน</h5>
          {!isCEO && <span className="small text-secondary"><i className="fa-solid fa-circle-info me-1" />เฉพาะ CEO เท่านั้นที่อนุมัติ/ยกเลิกได้</span>}
        </div>
        <div className="table-responsive table-scroll-mobile">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th className="text-secondary fw-medium rounded-start">รหัส / ชื่อรอบ</th>
                <th className="text-secondary fw-medium">งวด</th>
                <th className="text-secondary fw-medium text-center">วันที่จ่าย</th>
                <th className="text-secondary fw-medium text-center">พนักงาน</th>
                <th className="text-secondary fw-medium text-end">ยอดสุทธิ</th>
                <th className="text-secondary fw-medium text-center">สถานะ</th>
                <th className="text-secondary fw-medium text-center rounded-end">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-secondary py-5">
                    <i className="fa-regular fa-folder-open me-2" />ยังไม่มีรอบเงินเดือน
                  </td>
                </tr>
              )}
              {data.map((row) => (
                <tr key={row.uid}>
                  <td data-label="รหัส / ชื่อรอบ">
                    <div className="fw-semibold text-primary">{row.payroll_code}</div>
                    <div className="small text-secondary">{row.name || "-"}</div>
                  </td>
                  <td data-label="งวด">
                    <span className="small">{format_date(row.period_start, "dd/mm/yyyy")} → {format_date(row.period_end, "dd/mm/yyyy")}</span>
                  </td>
                  <td data-label="วันที่จ่าย" className="text-center">{row.pay_date ? format_date(row.pay_date, "dd/mm/yyyy") : "-"}</td>
                  <td data-label="พนักงาน" className="text-center">{row.total_employees} คน</td>
                  <td data-label="ยอดสุทธิ" className="text-end fw-bold">{toFixed(row.total_net)}</td>
                  <td data-label="สถานะ" className="text-center"><StatusBadge status={row.status} /></td>
                  <td data-label="จัดการ" className="text-center">
                    <div className="d-inline-flex gap-2">
                      <button onClick={() => handleView(row)} className="btn btn-sm btn-outline-secondary rounded-3" title="ดูข้อมูล">
                        <i className="fa-solid fa-eye" />
                      </button>
                      <button onClick={() => openSlipList(row)} className="btn btn-sm btn-outline-primary rounded-3" title="สลิปเงินเดือน">
                        <i className="fa-solid fa-file-invoice-dollar" />
                      </button>
                      {/* รวมสลิปทุกคนในรอบเป็น zip (1 พนักงาน = 1 ไฟล์ PDF) */}
                      <button
                        onClick={() => downloadSlipsZip(row)}
                        className="btn btn-sm btn-outline-danger rounded-3 text-nowrap"
                        title="ดาวน์โหลดสลิปทั้งหมด (zip)"
                        disabled={!!zipProgress || !row.total_employees}
                      >
                        {zipProgress?.uid === row.uid ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin me-1" />{zipProgress.current}/{zipProgress.total}
                          </>
                        ) : (
                          <i className="fa-solid fa-file-zipper" />
                        )}
                      </button>
                      {row.status === "pending" && isCEO && (
                        <>
                          <button onClick={() => handleApprove(row)} className="btn btn-sm btn-success rounded-3" title="อนุมัติ">
                            <i className="fa-solid fa-check" />
                          </button>
                          <button onClick={() => handleReject(row)} className="btn btn-sm btn-outline-danger rounded-3" title="ยกเลิก">
                            <i className="fa-solid fa-xmark" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={showView} onHide={() => setShowView(false)} centered size="xl" dialogClassName="modal-dialog-scrollable">
        <Modal.Header closeButton className="border-0 bg-light">
          <Modal.Title className="h5 fw-bold mb-0">
            รายละเอียดรอบเงินเดือน {viewData?.payroll_code ? `· ${viewData.payroll_code}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewData && (
            <>
              {/* ข้อมูลหัวรอบ */}
              <div className="row g-3 mb-4">
                <div className="col-6 col-md-4">
                  <div className="small text-secondary">ชื่อรอบ</div>
                  <div className="fw-semibold">{viewData.name || "-"}</div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="small text-secondary">งวด</div>
                  <div className="fw-semibold">{format_date(viewData.period_start, "dd/mm/yyyy")} → {format_date(viewData.period_end, "dd/mm/yyyy")}</div>
                </div>
                <div className="col-6 col-md-2">
                  <div className="small text-secondary">วันที่จ่าย</div>
                  <div className="fw-semibold">{viewData.pay_date ? format_date(viewData.pay_date, "dd/mm/yyyy") : "-"}</div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="small text-secondary">สถานะ</div>
                  <StatusBadge status={viewData.status} />
                </div>
              </div>

              {/* สรุปยอด */}
              <div className="row g-3 mb-4">
                {[
                  { label: "พนักงาน", value: `${viewData.total_employees} คน`, color: "#0c76e5" },
                  { label: "เงินได้รวม", value: toFixed(viewData.total_earnings), color: "#1f9d57" },
                  { label: "รายการหักรวม", value: toFixed(viewData.total_deductions), color: "#e54848" },
                  { label: "ยอดสุทธิ", value: toFixed(viewData.total_net), color: "#0b3150" },
                ].map((c) => (
                  <div key={c.label} className="col-6 col-md-3">
                    <div className="rounded-4 p-3" style={{ background: "#f7fbff", border: "1px solid rgba(18,125,255,0.10)" }}>
                      <div className="small text-secondary">{c.label}</div>
                      <div className="fw-bold" style={{ color: c.color }}>{c.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {viewData.status === "cancelled" && viewData.rejection_reason && (
                <div className="alert alert-danger py-2 small">เหตุผลที่ยกเลิก: {viewData.rejection_reason}</div>
              )}

              {/* ตารางรายการพนักงาน */}
              <div className="table-responsive table-scroll-mobile">
                <table className="table table-sm align-middle">
                  <thead className="table-light">
                    <tr className="text-secondary">
                      <th>พนักงาน</th>
                      <th className="text-end text-success">เงินเดือน</th>
                      <th className="text-end text-success">ค่าล่วงเวลา</th>
                      <th className="text-end text-success">โบนัส</th>
                      <th className="text-end text-danger">ภาษีหัก ณ ที่จ่าย</th>
                      <th className="text-end text-danger">ประกันสังคม</th>
                      <th className="text-end text-danger">อื่นๆ</th>
                      <th className="text-end">ยอดสุทธิ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewData.items || []).map((it) => (
                      <tr key={it.uid}>
                        <td data-label="พนักงาน">
                          <div className="fw-semibold">{it.employee_name || "-"}</div>
                          <div className="small text-secondary">{it.position || "-"}</div>
                        </td>
                        <td data-label="เงินเดือน" className="text-end">{toFixed(it.salary)}</td>
                        <td data-label="ค่าล่วงเวลา" className="text-end">{toFixed(it.overtime)}</td>
                        <td data-label="โบนัส" className="text-end">{toFixed(it.bonus)}</td>
                        <td data-label="ภาษีหัก ณ ที่จ่าย" className="text-end text-danger">{toFixed(it.wht)}</td>
                        <td data-label="ประกันสังคม" className="text-end text-danger">{toFixed(it.sso)}</td>
                        <td data-label="อื่นๆ" className="text-end text-danger">{toFixed(it.other)}</td>
                        <td data-label="ยอดสุทธิ" className="text-end fw-bold">{toFixed(it.net_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 bg-light">
          <button type="button" className="btn btn-secondary" onClick={() => setShowView(false)}>ปิด</button>
        </Modal.Footer>
      </Modal>

      {/* Modal: รายการสลิปเงินเดือนของรอบ */}
      <Modal show={showSlipList} onHide={() => setShowSlipList(false)} centered size="lg" dialogClassName="modal-dialog-scrollable">
        <Modal.Header closeButton className="border-0 bg-light">
          <Modal.Title className="h5 fw-bold mb-0">
            สลิปเงินเดือน {slipPayroll?.payroll_code ? `· ${slipPayroll.payroll_code}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <span className="small text-secondary">
              <i className="fa-solid fa-circle-info me-1" />ส่งสลิปไปยังอีเมลของพนักงานแต่ละคน
            </span>
            <button onClick={handleSendAll} className="btn btn-sm btn-primary rounded-3">
              <i className="fa-solid fa-paper-plane me-2" />ส่งเมลทั้งหมด
            </button>
          </div>
          <div className="table-responsive table-scroll-mobile">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr className="text-secondary">
                  <th>พนักงาน</th>
                  <th>อีเมล</th>
                  <th className="text-center">สลิป</th>
                </tr>
              </thead>
              <tbody>
                {(slipPayroll?.items || []).map((it) => (
                  <tr key={it.uid}>
                    <td data-label="พนักงาน">
                      <div className="fw-semibold">{it.employee_name || "-"}</div>
                      <div className="small text-secondary">{it.position || "-"}</div>
                    </td>
                    <td data-label="อีเมล" className="small">{it.employee?.email || <span className="text-danger">ไม่มีอีเมล</span>}</td>
                    <td data-label="สลิป" className="text-center">
                      <div className="d-inline-flex gap-2">
                        <button onClick={() => openSlipView(it)} className="btn btn-sm btn-outline-secondary rounded-3" title="ดูสลิป">
                          <i className="fa-solid fa-eye" />
                        </button>
                        <button onClick={() => handleSendOne(it)} className="btn btn-sm btn-outline-primary rounded-3" title="ส่งเมล" disabled={!it.employee?.email}>
                          <i className="fa-solid fa-envelope" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 bg-light">
          <button type="button" className="btn btn-secondary" onClick={() => setShowSlipList(false)}>ปิด</button>
        </Modal.Footer>
      </Modal>

      {/* Modal: ดูสลิปเงินเดือนรายคน + โหลด PDF */}
      <Modal show={showSlipView} onHide={() => setShowSlipView(false)} centered size="lg" dialogClassName="modal-dialog-scrollable">
        <Modal.Header closeButton className="border-0 bg-light">
          <Modal.Title className="h6 fw-bold mb-0">สลิปเงินเดือน · {slipItem?.employee_name || ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#eef3f8" }}>
          <div className="d-flex justify-content-center overflow-auto">
            <PayslipDocument ref={slipRef} company={company} payroll={slipPayroll} item={slipItem} />
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 bg-light">
          <button type="button" className="btn btn-secondary" onClick={() => setShowSlipView(false)}>ปิด</button>
          <button type="button" className="btn btn-outline-primary" onClick={() => slipItem && handleSendOne(slipItem)} disabled={!slipItem?.employee?.email}>
            <i className="fa-solid fa-envelope me-2" />ส่งเมล
          </button>
          <button type="button" className="btn btn-primary" onClick={handleDownloadSlip}>
            <i className="fa-solid fa-download me-2" />โหลด PDF
          </button>
        </Modal.Footer>
      </Modal>

      {/* hidden render target สำหรับทำ zip — ต้องอยู่ใน DOM จริง html2canvas ถึงจะวัดขนาดได้ */}
      <div style={{ position: "absolute", left: -99999, top: 0 }} aria-hidden="true">
        <PayslipDocument ref={zipSlipRef} company={company} payroll={zipPayroll} item={zipItem} />
      </div>

    </AppShell>
  );
}
